/**
 * Compact cookie card (bottom-left). Reject is as easy as accept.
 * Analytics and session replay are separate; replay is off by default.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import {
  COOKIE_BANNER_OPEN_EVENT,
  CONSENT_ACCEPTED,
  CONSENT_REJECTED,
  getCookieConsent,
  getCookiePreferences,
  setCookiePreferences,
} from '../utils/cookieConsent';
import { updateGaConsent } from '../utils/consentMode';
import './CookieBanner.css';

const CookieBanner = () => {
  const { t, localePath } = useLanguage();
  const [visible, setVisible] = useState(() => getCookieConsent() == null);
  const [required, setRequired] = useState(false);
  const [sessionReplay, setSessionReplay] = useState(() => {
    const prefs = getCookiePreferences();
    return prefs?.sessionReplay === true;
  });

  useEffect(() => {
    const onOpen = (event) => {
      const prefs = getCookiePreferences();
      setSessionReplay(prefs?.sessionReplay === true);
      setVisible(true);
      setRequired(Boolean(event.detail?.required));
    };
    window.addEventListener(COOKIE_BANNER_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(COOKIE_BANNER_OPEN_EVENT, onOpen);
  }, []);

  const handleReject = () => {
    setCookiePreferences({ analytics: false, sessionReplay: false });
    updateGaConsent(CONSENT_REJECTED);
    setVisible(false);
    setRequired(false);
  };

  const handleAccept = () => {
    setCookiePreferences({ analytics: true, sessionReplay });
    updateGaConsent(CONSENT_ACCEPTED);
    setVisible(false);
    setRequired(false);
  };

  if (!visible) return null;

  return (
    <>
      {required ? <div className="cookie-banner__backdrop" aria-hidden="true" /> : null}
      <div
        className={`cookie-banner${required ? ' cookie-banner--required' : ''}`}
        role="region"
        aria-label={t('cookies.regionLabel')}
      >
        {required ? (
          <p className="cookie-banner__label cookie-banner__label--required">
            {t('cookies.requiredLead')}
          </p>
        ) : null}
        <p className="cookie-banner__label">{t('cookies.regionLabel')}</p>
        <p className="cookie-banner__text">
          {t('cookies.body')}{' '}
          <Link to={localePath('/privacy')} className="cookie-banner__link">
            {t('cookies.privacy')}
          </Link>
        </p>
        <label className="cookie-banner__check">
          <input
            type="checkbox"
            checked={sessionReplay}
            onChange={(e) => setSessionReplay(e.target.checked)}
          />
          <span>{t('cookies.sessionReplay')}</span>
        </label>
        <div className="cookie-banner__actions">
          <button
            type="button"
            className="cookie-banner__btn cookie-banner__btn--reject"
            onClick={handleReject}
          >
            {t('cookies.reject')}
          </button>
          <button
            type="button"
            className="cookie-banner__btn cookie-banner__btn--accept"
            onClick={handleAccept}
          >
            {t('cookies.accept')}
          </button>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
